import util from '../util';
import Widget from './Widget';

export default class ViewerCountWidget extends Widget {
	constructor({ container }) {
		super({ container });
		this.chats = [];
		this._allChannels = [];
	}

	async init(initialData = {}) {
		await super.init(initialData);
	}

	_build() {
		this.container.innerHTML = '<div class="viewer-count-widget"></div>';
		this._widgetEl = this.container.querySelector('.viewer-count-widget');
	}

	destroy() {
		document.getElementById(`viewerCountStyle-${this.container.id}`)?.remove();
		super.destroy();
	}

	applySettings(settings) {
		super.applySettings(settings);
		this._applyChannelFilter();
	}

	// ── Public data methods ──────────────────────────────────────────────────

	setChannels(channels) {
		this._allChannels = channels;
		this._applyChannelFilter();
	}

	setChannelInfo(serviceId, token, info) {
		const chat = this.chats.find(c => c.service_id === serviceId && c.token === token);
		if(chat) { chat.info = info; this.render(); }
	}

	// ── Internal ─────────────────────────────────────────────────────────────

	_applyChannelFilter() {
		const s = this.settings;
		const toShow = this._allChannels.filter(c =>
			(s.showDisabledChannels || c.status === 'enabled')
			&& (!s.showOnlyVerifiedChannels || c.verified)
			&& !c.no_streaming
			&& !(s.excludedChannels?.includes(`${c.service_id}:${c.token}`))
		);

		this.chats = toShow.map(c => {
			const existing = this.chats.find(h => h.service_id === c.service_id && h.token === c.token);
			return { ...c, info: existing?.info || null, error: existing?.error || false };
		});

		this.render();
	}

	render() {
		if(!this._widgetEl) return;
		this._widgetEl.innerHTML = '';

		const s = this.settings;
		const logoOnRight = s.logoPosition === 'right';

		let chats = this.chats.filter(chat => s.showOfflineChannels || chat.info?.numLive > 0);
		// Default (empty) keeps the channel-list order (the user's channel `position`); other
		// values re-sort by name or live viewer count.
		if(s.sort === 'name') chats = chats.slice().sort((a, b) => (a.service_name || '').localeCompare(b.service_name || '') || (a.name || '').localeCompare(b.name || ''));
		if(s.sort === 'asc')  chats = chats.slice().sort((a, b) => (a.info?.numViewers || 0) - (b.info?.numViewers || 0));
		if(s.sort === 'desc') chats = chats.slice().sort((a, b) => (b.info?.numViewers || 0) - (a.info?.numViewers || 0));

		for(const chat of chats) {
			const chatEl = document.createElement('span');
			chatEl.className = 'chat' + (chat.error ? ' error' : '') + (logoOnRight ? ' right' : '');
			chatEl.title = chat.name || '';

			if(!logoOnRight) {
				if(chat.service_icon_url) {
					const avatar = document.createElement('span');
					avatar.className = 'avatar';
					avatar.style.backgroundImage = `url(${chat.service_icon_url})`;
					chatEl.appendChild(avatar);
				} else {
					const b = document.createElement('b');
					b.textContent = `${chat.service_name}:`;
					chatEl.appendChild(b);
				}
			}

			if(chat.info && chat.info.numLive > 0) {
				const count = document.createElement('span');
				count.textContent = chat.info.numViewers === null ? '?' : (chat.info.numViewers || 0);
				chatEl.appendChild(count);
			} else {
				const i = document.createElement('i');
				i.textContent = chat.error ? 'error' : '×';
				chatEl.appendChild(i);
			}

			if(logoOnRight) {
				if(chat.service_icon_url) {
					const avatar = document.createElement('span');
					avatar.className = 'avatar';
					avatar.style.backgroundImage = `url(${chat.service_icon_url})`;
					chatEl.appendChild(avatar);
				} else {
					const b = document.createElement('b');
					b.textContent = `${chat.service_name}:`;
					chatEl.appendChild(b);
				}
			}

			this._widgetEl.appendChild(chatEl);
		}
	}

	_generateCSS() {
		const s = this.settings;
		const id = this.container.id;
		const isVertical = s.orientation === 'vertical';

		const css = `
			#${id} {
				font-family: Arial, sans-serif;
				font-size: 18px;
				color: white;
				margin: 0;
				padding: 0;
				width: 100%;
				height: 100%;
				${s.textColor   ? `color: ${s.textColor};` : ''}
				${s.fontFamily  ? `font-family: ${s.fontFamily};` : ''}
				${s.fontSize > 0 ? `font-size: ${s.fontSize}px; line-height: ${s.fontSize}px;` : ''}
				${s.letterSpacing > 0 ? `letter-spacing: ${s.letterSpacing}px;` : ''}
				${s.boldText    ? `font-weight: bold;` : ''}
				${s.italicText  ? `font-style: italic;` : ''}
			}
			#${id} .viewer-count-widget {
				display: flex;
				flex-wrap: wrap;
				flex-direction: ${isVertical ? 'column' : 'row'};
				justify-content: ${s.justifyContent || 'flex-start'};
				align-items: ${s.alignItems || 'center'};
				align-content: ${s.alignItems || 'center'};
				width: 100%;
				height: 100%;
			}
			#${id} .chat {
				padding: 4px;
				border-radius: 8px;
				background-color: rgba(0, 128, 0, 0);
				margin: 2px;
				${s.textBg ? `background-color: ${s.textBg};` : ''}
				${s.fontSize > 0 ? `padding: ${Math.ceil(s.fontSize/5)}px;` : ''}
				${s.fontSize > 0 ? `border-radius: ${Math.ceil(s.fontSize/2.5)}px;` : ''}
				${s.fontSize > 0 ? `margin: ${Math.ceil(s.fontSize/10)}px;` : ''}
				${s.underlineText || s.strikethroughText ? `text-decoration: ${s.underlineText ? 'underline' : ''} ${s.strikethroughText ? 'line-through' : ''};` : ''}
				${s.blurRadius > 0 || s.spreadRadius > 0 ? `filter: ${util.generateSpreadShadowFilter(s.spreadRadius, s.blurRadius, s.shadowColor)};` : ''}
			}
			#${id} .chat.error { background: red; }
			#${id} .chat .avatar {
				width: 16px;
				height: 16px;
				margin-right: 4px;
				vertical-align: top;
				display: inline-block;
				background-size: cover;
				${s.fontSize > 0 ? `width: ${s.fontSize}px; height: ${s.fontSize}px; margin-right: ${Math.ceil(s.fontSize/5)}px;` : ''}
			}
			#${id} .chat.right .avatar {
				margin-right: 0;
				margin-left: 4px;
				${s.fontSize > 0 ? `margin-left: ${Math.ceil(s.fontSize/5)}px;` : ''}
			}
			${s.extraCSS || ''}
		`;

		this._setCSS(`viewerCountStyle-${id}`, css);
		this._updateFontLink();
	}
}
